import { Injectable } from '@angular/core';
import { ApplicationUser } from '../../../authentication/viewmodels/application-user';
import { PcnModel } from '../viewmodels/pcn.model';
import { ReasonModel } from '../viewmodels/reason.model';
import { QuestionAndAnswerModel } from '../viewmodels/questionAndAnswer.model';
import { AlertModel } from '../viewmodels/alert.model';

@Injectable()
export class StorageService {
  /* Local Storage (Common Between Tabs) */

  getAccessToken(): string {
    if (this.localStorageItemExists('rm_access_token')) {
      return localStorage.getItem('rm_access_token');
    }
    return null;
  }
  setAccessToken(token: string): void {
    localStorage.setItem('rm_access_token', token);
  }

  getTokenExpirationDate(): Date {
    const date = localStorage.getItem('rm_token_expiration_date');
    return date ? new Date(date) : null;
  }

  setTokenExpirationDate(date: Date | string): void {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    localStorage.setItem('rm_token_expiration_date', dateObj.toISOString());
  }

  getCurrentUser(): ApplicationUser {
    const user = localStorage.getItem('rm_current_user');
    return user ? (JSON.parse(user) as ApplicationUser) : null;
  }
  setCurrentUser(user: ApplicationUser): void {
    localStorage.setItem('rm_current_user', JSON.stringify(user));
  }

  getLocalStorageItem(key: string): string {
    return localStorage.getItem(key) || '';
  }
  setLocalStorageItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  localStorageItemExists(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  clearLocalStorageItem(key: string): void {
    localStorage.removeItem(key);
  }

  clearLocalStorage(): void {
    localStorage.removeItem('rm_access_token');
    localStorage.removeItem('rm_current_user');
    localStorage.removeItem('rm_token_expiration_date');
  }

  /* Session Storage (Unique to Tab) */

  getPcn(): PcnModel {
    const user = sessionStorage.getItem('rm_pcn');
    return user ? (JSON.parse(user) as PcnModel) : null;
  }
  setPcn(user: PcnModel): void {
    sessionStorage.setItem('rm_pcn', JSON.stringify(user));
  }
  isPcnProcessing(): boolean {
    if (sessionStorage.getItem('rm_pcn_processing')) {
      return JSON.parse(sessionStorage.getItem('rm_pcn_processing'));
    }
    // default to false
    this.setPcnProcessing(false);
    return false;
  }
  setPcnProcessing(processing: boolean): void {
    sessionStorage.setItem('rm_pcn_processing', JSON.stringify(processing));
  }
  isPcnClosed(): boolean {
    if (sessionStorage.getItem('rm_pcn_closed')) {
      return JSON.parse(sessionStorage.getItem('rm_pcn_closed'));
    }
    return false;
  }
  setPcnClosed(close: boolean): void {
    sessionStorage.setItem('rm_pcn_closed', JSON.stringify(close));
  }

  // getCustomParagraphs(): AlertModel[] {
  //   const customParagraphs = sessionStorage.getItem('pcn_custom_paragraphs');
  //   return customParagraphs ? (JSON.parse(customParagraphs) as []) : null;
  // }
  // setCustomParagraphs(customParagraphs: AlertModel[]): void {
  //   sessionStorage.setItem('pcn_custom_paragraphs', JSON.stringify(customParagraphs));
  // }

  getReasons(): ReasonModel[] {
    if (!this.sessionStorageItemExists('rm_reasons')) {
      this.setReasons([]);
    }

    const reasons = sessionStorage.getItem('rm_reasons');
    return reasons ? (JSON.parse(reasons) as ReasonModel[]) : null;
  }
  setReasons(reasons: ReasonModel[]): void {
    sessionStorage.setItem('rm_reasons', JSON.stringify(reasons));
  }

  getQuestionsAndAnswers(): QuestionAndAnswerModel[] {
    const questionsAndAnswers = sessionStorage.getItem('rm_questions_answers');
    return questionsAndAnswers
      ? (JSON.parse(questionsAndAnswers) as QuestionAndAnswerModel[])
      : null;
  }
  setQuestionsAndAnswers(questionsAndAnswers: QuestionAndAnswerModel[]): void {
    sessionStorage.setItem('rm_questions_answers', JSON.stringify(questionsAndAnswers));
  }

  isReasonsLocked(): boolean {
    if (sessionStorage.getItem('rm_reasons_locked')) {
      return JSON.parse(sessionStorage.getItem('rm_reasons_locked'));
    }
    return false;
  }

  lockReasons(lock: boolean): void {
    sessionStorage.setItem('rm_reasons_locked', JSON.stringify(lock));
  }

  isLetterTopLocked(): boolean {
    if (sessionStorage.getItem('rm_letter_top_locked')) {
      return JSON.parse(sessionStorage.getItem('rm_letter_top_locked'));
    }
    return false;
  }

  lockLetterTop(lock: boolean): void {
    sessionStorage.setItem('rm_letter_top_locked', JSON.stringify(lock));
  }

  isPcnPanelLocked(): boolean {
    try {
      const raw = localStorage.getItem('rm_pcn_panel_locked');
      return raw ? JSON.parse(raw) === true : false;
    } catch {
      return false;
    }
  }

  lockPcnPanel(lock: boolean): void {
    localStorage.setItem('rm_pcn_panel_locked', JSON.stringify(lock));
  }

  isPcnDetailsLocked(): boolean {
    if (this.sessionStorageItemExists('rm_pcn_details_locked')) {
      return JSON.parse(sessionStorage.getItem('rm_pcn_details_locked'));
    }
    return false;
  }

  lockPcnDetails(lock: boolean): void {
    sessionStorage.setItem('rm_pcn_details_locked', JSON.stringify(lock));
  }

  lockPcnInput(lock: boolean): void {
    sessionStorage.setItem('rm_pcn_input_locked', JSON.stringify(lock));
  }
  isPcnInputLocked(): boolean {
    if (this.sessionStorageItemExists('rm_pcn_input_locked')) {
      return JSON.parse(sessionStorage.getItem('rm_pcn_input_locked'));
    }
    return false;
  }

  removeScreenLocks(): void {
    this.lockReasons(false);
    this.lockLetterTop(false);
    this.lockPcnDetails(false);
  }

  getSessionStorageItem(key: string): string {
    return sessionStorage.getItem(key) || '';
  }
  setSessionStorageItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  sessionStorageItemExists(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  clearSessionStorageItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  clearSessionStorage(): void {
    sessionStorage.removeItem('rm_pcn');
    sessionStorage.removeItem('rm_reasons');
  }
}
